import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { switchMap, filter, map, mergeMap, take, timeout, share, distinctUntilChanged, withLatestFrom, tap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { UserDataEntity } from '@zerops/zerops/core/user-data-base';
import {
  firstClassRecipeImport,
  serviceStackRestart,
  ServiceStackOperationTypes
} from '@zerops/zerops/core/service-stack-base';
import { FEATURE_NAME } from './zcp-agent-auth-dialog.constant';
import { zcpAgentAuthDialogActions, zcpAgentAuthDialogOpen, selectAuthedAgentsByStack } from './zcp-agent-auth-dialog.state';
import { hasAuthHandler } from './zcp-agent-auth-dialog.handlers';
import {
  agentTypeToEnvSuffix,
  AGENT_PROVIDER_MAP,
  PROVIDER_ENV_VAR_MAP,
  ZagentAgentType
} from '@zerops/zerops/core/zerops-services';
import { ContainerEntity } from '@zerops/zerops/core/container-base';
import { processFinishNotify } from '@zerops/zerops/core/process-base';
import { ServiceStackTypes } from '@zerops/models/service-stack';
import { ProcessActions, Process } from '@zerops/models/process';
import { UserData } from '@zerops/models/user-data';

@Injectable()
export class ZcpAgentAuthDialogEffect {

  // # Deps
  #actions$ = inject(Actions);
  #store = inject(Store);
  #containerEntity = inject(ContainerEntity);
  #userDataEntity = inject(UserDataEntity);

  // # Per-tab tracking
  // Maps projectIds → OAuth-mode agent list for ZCP deploys THIS tab initiated.
  // Prevents the auth dialog from opening in tabs that didn't start the deploy
  // (processFinishNotify fires in ALL tabs via independent websocket
  // connections), AND carries the agent queue from form-submit time so the
  // auto-open effect doesn't have to round-trip through userData. The queue is
  // the OAuth subset of `zcpAgents` from the dispatched action payload.
  #pendingZcpProjectAgents = new Map<string, ZagentAgentType[]>();

  // # Event Streams

  /**
   * Track ZCP deploys initiated from this tab.
   * firstClassRecipeImport is dispatched in the tab that started the deploy,
   * so only that tab's map will contain the projectId. The `zcpAgents` payload
   * field carries the OAuth-mode agent list that the auto-open effect uses as
   * the walker queue source — no userData round-trip required.
   */
  trackZcpDeploy$ = createEffect(() => this.#actions$.pipe(
    ofType(firstClassRecipeImport),
    filter(({ data }) => !!data.projectId),
    tap(({ data }) => {
      this.#pendingZcpProjectAgents.set(data.projectId, data.zcpAgents ?? []);
    })
  ), { dispatch: false });

  /**
   * Source: ZCP stack.build process finished.
   * At this point the stack is ACTIVE and containers exist.
   * Fires on fresh deploy AND on page refresh (websocket replays FINISHED processes).
   * Shared across effects to prevent duplicate subscriptions.
   */
  #zcpBuildFinished$ = this.#actions$.pipe(
    ofType(processFinishNotify),
    map(({ data }) => data.find((p: Partial<Process>) =>
      p.actionName === ProcessActions.stackBuild
      && p.serviceStacks?.some((ss) => ss.serviceStackTypeId === ServiceStackTypes.Zcp)
    )),
    filter((process): process is Partial<Process> & { serviceStackId: string; projectId: string; clientId: string } =>
      !!process?.serviceStackId && !!process?.projectId && !!process?.clientId
    ),
    map((process) => ({
      serviceStackId: process.serviceStackId,
      projectId: process.projectId,
      clientId: process.clientId
    })),
    distinctUntilChanged((a, b) => a.serviceStackId === b.serviceStackId),
    share()
  );

  /**
   * Subscribe to the project's userData so mark/revoke writes are reflected
   * locally and the service card stays in sync after a deploy. The
   * auto-open path no longer reads anything from userData — agents come
   * via the `firstClassRecipeImport` payload — but we keep the subscription
   * live so subsequent writes update the per-agent rows in real time.
   */
  onZcpBuildLoadUserData$ = createEffect(() => this.#zcpBuildFinished$.pipe(
    map(({ clientId, projectId }) =>
      this.#userDataEntity.listSubscribe(
        clientId,
        { name: FEATURE_NAME, id: projectId },
        {
          search: [{
            name: 'projectId',
            operator: 'eq',
            value: projectId
          }]
        }
      )
    )
  ));

  /**
   * Load containers for the ZCP stack (needed for terminal containerId).
   */
  onZcpBuildLoadContainers$ = createEffect(() => this.#zcpBuildFinished$.pipe(
    map(({ clientId, serviceStackId }) =>
      this.#containerEntity.listSubscribe(
        clientId,
        { name: FEATURE_NAME, id: serviceStackId },
        {
          search: [{
            name: 'serviceStackId',
            operator: 'eq',
            value: serviceStackId
          }]
        }
      )
    )
  ));

  /**
   * Open the auth dialog with the deploy-time OAuth agent queue once the
   * ZCP stack build finishes. Only fires in the tab that initiated the
   * deploy (the only tab whose `#pendingZcpProjectAgents` map has the
   * projectId entry).
   *
   * The queue source is the `zcpAgents` payload that
   * `firstClassRecipeImport` carries — captured at form-submit time and
   * stashed in `trackZcpDeploy$`. We further filter to agents that have a
   * registered auth handler AND aren't already authed in-session. There
   * is no userData read here: the deploy-time intent is the form's truth,
   * not whatever userData has finished syncing.
   */
  onZcpNeedsAuthOpenDialog$ = createEffect(() => this.#zcpBuildFinished$.pipe(
    filter(({ projectId }) => this.#pendingZcpProjectAgents.has(projectId)),
    withLatestFrom(this.#store.pipe(select(selectAuthedAgentsByStack))),
    switchMap(([ { serviceStackId, projectId }, authedByStack ]) => {
      const captured = this.#pendingZcpProjectAgents.get(projectId) ?? [];
      const alreadyAuthedInSession = authedByStack[serviceStackId] ?? [];
      const queue = captured.filter((t) =>
        hasAuthHandler(t) && !alreadyAuthedInSession.includes(t)
      );

      if (queue.length === 0) return EMPTY;

      return this.#containerEntity.list$(
        { name: FEATURE_NAME, id: serviceStackId }
      ).pipe(
        map((containers) => containers?.[0]),
        filter((container): container is NonNullable<typeof container> => !!container),
        take(1),
        timeout({ each: 10_000, with: () => EMPTY }),
        // Consume the deploy's pending agents once we actually open the dialog,
        // so a later rebuild of the same project doesn't re-trigger this with a
        // stale queue. (Left intact on container timeout so that path can retry.)
        tap(() => this.#pendingZcpProjectAgents.delete(projectId)),
        map((container) => zcpAgentAuthDialogOpen({
          containerId: container.id,
          serviceStackId,
          agentType: queue[0],
          agentTypes: queue
        }))
      );
    })
  ));

  /**
   * Shared manual-open stream — gated on a supported agent handler.
   * Two effects consume it: one subscribes containers, the other reads
   * them and opens the dialog. Without explicit container subscription
   * list$() returns empty and the open silently times out.
   */
  #manualOpen$ = this.#actions$.pipe(
    ofType(zcpAgentAuthDialogActions.manualOpen),
    filter(({ agentType }) => hasAuthHandler(agentType)),
    share()
  );

  /**
   * Subscribe containers for the stack so the open effect can read them.
   * Mirrors onZcpBuildLoadContainers$ but for the manual reauth path.
   */
  onManualOpenSubscribeContainers$ = createEffect(() => this.#manualOpen$.pipe(
    map(({ serviceStackId, clientId }) => this.#containerEntity.listSubscribe(
      clientId,
      { name: FEATURE_NAME, id: serviceStackId },
      {
        search: [{
          name: 'serviceStackId',
          operator: 'eq',
          value: serviceStackId
        }]
      }
    ))
  ));

  /**
   * Manual open: read first container, dispatch dialog open.
   * Triggered from the service stack card "Manually authorize" button and from
   * the code-server agent-auth bridge (which passes a `requestId`).
   * Passes a 1-element `agentTypes` list — the degenerate case for the
   * Phase 2 walker. When the service card learns to pick a specific agent
   * (1F refactor), it can pass a multi-element list here too.
   *
   * `mergeMap` (not `switchMap`): the bridge can relay several webview asks in
   * quick succession, and each must run to its own result — a newer request must
   * never cancel an older one still resolving its container, or that older
   * request's caller (which is waiting on `manualOpenResult` to ack) would hang.
   *
   * For a correlated caller (`requestId` present) the effect emits a
   * `manualOpenResult` alongside the open on success, and — instead of a silent
   * `EMPTY` — an explicit `ok:false` result on container timeout/absence, so the
   * bridge can ack `not-ready` rather than let the sender time out. Uncorrelated
   * callers keep the old behaviour: dialog opens, nothing acked, silent on
   * timeout.
   */
  onManualOpenLoadAndOpen$ = createEffect(() => this.#manualOpen$.pipe(
    mergeMap(({ serviceStackId, agentType, agentTypes, requestId, successNavigation }) => {
      const openActions = (containerId: string): Action[] => {
        const open = zcpAgentAuthDialogOpen({
          containerId,
          serviceStackId,
          agentType,
          agentTypes: agentTypes ?? [ agentType ],
          successNavigation
        });
        return requestId
          ? [ open, zcpAgentAuthDialogActions.manualOpenResult({ requestId, ok: true }) ]
          : [ open ];
      };
      const failureActions = (): Action[] =>
        requestId
          ? [ zcpAgentAuthDialogActions.manualOpenResult({ requestId, ok: false }) ]
          : [];

      return this.#containerEntity.list$({ name: FEATURE_NAME, id: serviceStackId }).pipe(
        map((containers) => {
          // ZCP is single-container by design; more than one is unexpected —
          // warn and take the first deterministically rather than picking blind.
          if (containers && containers.length > 1) {
            console.warn(
              `[manual-open] ${containers.length} containers for stack ${serviceStackId}; taking the first`
            );
          }
          return containers?.[0];
        }),
        filter((container): container is NonNullable<typeof container> => !!container),
        take(1),
        map((container) => {
          if (requestId) {
            console.debug(`[manual-open] container resolved, dispatching dialog open (req ${requestId.slice(0, 8)})`);
          }
          return openActions(container.id);
        }),
        timeout({ each: 10_000, with: () => {
          if (requestId) {
            console.debug(`[manual-open] container unavailable within 10s (req ${requestId.slice(0, 8)})`);
          }
          return of(failureActions());
        } }),
        mergeMap((actions) => actions)
      );
    })
  ));

  /**
   * Mark as authorized — writes the per-agent `ZCP_AGENT_OAUTH_<SUFFIX>`
   * key. Looks up the existing userData entry to choose addOne vs
   * updateOne (avoiding the backend's duplicate-key error on re-mark).
   *
   * Dispatched by the walker (per-agent on each OAuth success) AND by the
   * service-card's manual "Mark as authorized" button. Token-mode agents
   * are considered authorized via `ZCP_AGENT_TOKEN_<SUFFIX>` presence
   * (written at deploy time / by the token-popover), so this effect is
   * OAuth-only.
   */
  onMarkAuthorized$ = createEffect(() => this.#actions$.pipe(
    ofType(zcpAgentAuthDialogActions.markAuthorized),
    filter((action): action is ReturnType<typeof zcpAgentAuthDialogActions.markAuthorized> & { agentType: ZagentAgentType } => !!action.agentType),
    withLatestFrom(this.#userDataEntity.rawEntities$()),
    map(([ { serviceStackId, agentType }, entities ]) => {
      const key = `ZCP_AGENT_OAUTH_${agentTypeToEnvSuffix(agentType)}`;
      const existing = entities
        ? (Object.values(entities) as UserData[]).find((d) =>
            d.serviceStackId === serviceStackId && d.key === key
          )
        : undefined;
      return existing
        ? this.#userDataEntity.updateOne(existing.id, { key, content: 'true', sensitive: false })
        : this.#userDataEntity.addOne({ key, content: 'true', serviceStackId, sensitive: false });
    })
  ));

  /**
   * Revoke an agent's authorization — deletes the durable
   * `ZCP_AGENT_OAUTH_<SUFFIX>` userData entry. The reducer also wipes the
   * in-session flag so the row flips back to unauthed instantly.
   * No-op if the entry doesn't exist (already absent → already unauthed).
   *
   * Token-mode authorizations are NOT revoked here — those live on
   * `ZCP_AGENT_TOKEN_<SUFFIX>`; clearing them is a separate flow.
   */
  onRevokeAuthorization$ = createEffect(() => this.#actions$.pipe(
    ofType(zcpAgentAuthDialogActions.revokeAuthorization),
    withLatestFrom(this.#userDataEntity.rawEntities$()),
    map(([ { serviceStackId, agentType }, entities ]) => {
      const key = `ZCP_AGENT_OAUTH_${agentTypeToEnvSuffix(agentType)}`;
      const existing = entities
        ? (Object.values(entities) as UserData[]).find((d) =>
            d.serviceStackId === serviceStackId && d.key === key
          )
        : undefined;
      return existing ? this.#userDataEntity.deleteOne(existing.id) : null;
    }),
    filter((a): a is NonNullable<typeof a> => a !== null)
  ));

  /**
   * Post-deploy "set API token" — writes (or updates) THREE userData
   * entries and triggers a restart so the runtime picks up the new env:
   *
   *   1. `ZCP_AGENT_TOKEN_<S>`     — the token value (single source of truth)
   *   2. `ZCP_AGENT_AUTH_TYPE_<S>` — `"token"` (marks the agent as token-mode)
   *   3. `<CLI_KEY>`               — provider-canonical CLI env (e.g.
   *      `OPENAI_API_KEY`, `ANTHROPIC_API_KEY`), literal copy of the token
   *
   * The YAML deploy path writes `<CLI_KEY>` as a zerops env reference
   * (`${ZCP_AGENT_TOKEN_<S>}`), but doing the same from userData would
   * rely on the backend resolving the reference in plain user-data
   * content — which isn't guaranteed. Writing the literal token to the
   * CLI key is mode-equivalent for the runtime and survives without
   * backend-side reference resolution.
   *
   * Each entry uses the same add-vs-update branch as `onMarkAuthorized$`
   * to avoid the backend's duplicate-key error on re-set.
   */
  onSetApiToken$ = createEffect(() => this.#actions$.pipe(
    ofType(zcpAgentAuthDialogActions.setApiToken),
    withLatestFrom(this.#userDataEntity.rawEntities$()),
    mergeMap(([ { serviceStackId, agentType, token }, entities ]) => {
      const suffix = agentTypeToEnvSuffix(agentType);
      const tokenKey = `ZCP_AGENT_TOKEN_${suffix}`;
      const typeKey = `ZCP_AGENT_AUTH_TYPE_${suffix}`;
      const provider = AGENT_PROVIDER_MAP[agentType];
      const cliKey = PROVIDER_ENV_VAR_MAP[provider] ?? 'API_KEY';

      const all = entities ? (Object.values(entities) as UserData[]) : [];
      const findFor = (key: string) =>
        all.find((d) => d.serviceStackId === serviceStackId && d.key === key);
      // `sensitive: false` even for the token value — the FE derives
      // `authorized` from the token env's content (extractServiceMetadataMulti)
      // and sensitive contents come back redacted from the list API.
      // Secret-looking keys are blurred client-side by z-env-content instead.
      // The provider CLI key is the exception: nothing in the FE reads its
      // content, and the user may own that env already — an update keeps
      // whatever sensitivity it has instead of silently downgrading it.
      const upsert = (key: string, content: string, sensitive: boolean) => {
        const existing = findFor(key);
        return existing
          ? this.#userDataEntity.updateOne(existing.id, { key, content, sensitive })
          : this.#userDataEntity.addOne({ key, content, serviceStackId, sensitive });
      };

      return [
        upsert(tokenKey, token, false),
        upsert(typeKey, 'token', false),
        upsert(cliKey, token, findFor(cliKey)?.sensitive ?? false),
        serviceStackRestart({
          data: { id: serviceStackId, type: ServiceStackOperationTypes.Restart }
        })
      ];
    })
  ));

}
